import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getData, postData, deleteData } from "../../../utility/Utility";
import Swal from "sweetalert2";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

function ManageDeleteRequests() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRequests, setTotalRequests] = useState(0);
  const rowsPerPage = 10;

  // Fetch pending deletion requests
  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await postData("api/users/pending-deletions", {
        page: currentPage + 1,
        limit: rowsPerPage,
        status: "pending",
      });

      if (response?.success) {
        setRequests(response.approvals);
        setTotalPages(response.pagination.totalPages);
        setTotalRequests(response.pagination.totalRequests);
      } else {
        toast.error(response?.message || "Failed to fetch deletion requests");
      }
    } catch (error) {
      toast.error("Error fetching deletion requests");
      console.error("Fetch requests error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle reject action
  const handleReject = async (id) => {
    const { value: reason, isConfirmed } = await Swal.fire({
      title: "Reject Request",
      input: "textarea",
      inputLabel: "Reason for rejection",
      inputPlaceholder: "Enter reason...",
      inputAttributes: { required: true },
      showCancelButton: true,
      confirmButtonText: "Confirm Rejection",
      cancelButtonText: "Cancel",
      inputValidator: (value) => !value && "Reason is required",
    });

    if (!isConfirmed) {
      toast.info("Rejection cancelled");
      return;
    }

    try {
      const response = await postData(`api/users/process-deletion`, {
        requestId: id,
        status: "rejected",
        reason,
      });

      if (response?.success) {
        toast.success("Request rejected successfully");
        fetchRequests(); // Refresh list
      } else {
        throw new Error(response?.message || "Failed to reject request");
      }
    } catch (error) {
      toast.error(error.message);
      console.error("Reject error:", error);
    }
  };

  // Handle approve action - navigate to agent selection page
  const handleApprove = (requestId) => {
    navigate(`/delete-requests/assign-agent/${requestId}`);
  };

  // Handle page change for main requests
  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  // Handle form submit for search
  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchRequests();
  };

  // Reset all filters
  const handleReset = () => {
    setSearchTerm("");
    setCurrentPage(0);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  useEffect(() => {
    fetchRequests();
  }, [currentPage]);

  return (
    <div className="content-page">
      {loading && <CustomLoader loading={loading} />}

      <ToastContainer position="top-right" autoClose={3000} />
      <div className="content">
        <div className="container-fluid">
          {/* Header Section */}
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <h4 className="fs-18 fw-semibold m-0">Manage Delete Requests</h4>
              <p className="text-muted mb-0">Total Requests: {totalRequests}</p>
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3 align-items-center">
                      <div className="col-md-12 col-lg-4">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control ps-4"
                            placeholder="Search by name, email or mobile..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <span className="input-group-text">
                            <i className="mdi mdi-magnify"></i>
                          </span>
                        </div>
                      </div>

                      <div className="col-md-12 col-lg-3 d-flex gap-2">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? (
                            <span className="spinner-border spinner-border-sm me-1"></span>
                          ) : null}
                          Search
                        </button>

                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleReset}
                          disabled={loading}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="row mt-2">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {!loading && (
                    <div className="table-responsive">
                      <table className="table-hover table-centered mb-0">
                        <thead>
                          <tr>
                            <th>Request ID</th>
                            <th>User</th>
                            <th>Contact</th>
                            <th>Reason</th>
                            <th>Requested On</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {requests.length > 0 ? (
                            requests.map((request) => (
                              <tr key={request._id}>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div className="flex-shrink-0 me-2">
                                      <div className="avatar-xs">
                                        <div className="avatar-title rounded-circle bg-light text-primary">
                                          {request.userId?.fullName
                                            ?.charAt(0)
                                            ?.toUpperCase() || "U"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex-grow-1">
                                      <h5 className="fs-14 mb-1">
                                        {request._id || "N/A"}
                                      </h5>
                                      <p className="text-muted mb-0">
                                        {request.title}
                                      </p>
                                    </div>
                                  </div>
                                </td>
                                <td>{request.userId?.fullName || "N/A"}</td>
                                <td>
                                  <p className="mb-1">
                                    {request.userId?.email || "N/A"}
                                  </p>
                                  <p className="text-muted mb-0">
                                    {request.userId?.mobile || "N/A"}
                                  </p>
                                </td>
                                <td>{request.reason || "N/A"}</td>
                                <td>{formatDate(request.createdAt)}</td>
                                <td>
                                  <span
                                    className={`badge bg-${
                                      request.status === "pending"
                                        ? "warning"
                                        : request.status === "approved"
                                        ? "success"
                                        : "danger"
                                    }`}
                                  >
                                    {request.status?.toUpperCase() || "N/A"}
                                  </span>
                                </td>
                                <td>
                                  {request.status === "pending" && (
                                    <div className="table-actions">
                                      <button
                                        className="btn btn-sm btn-success"
                                        onClick={() =>
                                          handleApprove(request._id)
                                        }
                                      >
                                        Approve
                                      </button>
                                      <button
                                        className="btn btn-sm btn-danger"
                                        onClick={() =>
                                          handleReject(request._id)
                                        }
                                      >
                                        Reject
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center py-4">
                                No pending deletion requests found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <ReactPaginate
                          previousLabel={
                            <i className="mdi mdi-chevron-left"></i>
                          }
                          nextLabel={<i className="mdi mdi-chevron-right"></i>}
                          breakLabel="..."
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          pageCount={totalPages}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName="pagination pagination-rounded justify-content-end mt-3"
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ManageDeleteRequests;
